import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorResponse } from "oidc-client-ts";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AuthStateProvider, type AuthState } from "../auth/AuthContext";
import { completeCallbackOnce } from "../auth/callback";
import { securityPrincipalKey } from "../auth/roles";
import { useLogin } from "../auth/useLogin";
import {
  authState,
  completeSignIn,
  initializeAuth,
  isAuthEnabled,
  subscribeAuth,
} from "../lib/auth";
import { createAppQueryClient } from "../queryClient";
import "./auth.css";

type AuthView =
  | { kind: "checking" }
  | { kind: "callback" }
  | { kind: "authed"; auth: AuthState }
  | { kind: "signed-out" }
  | { kind: "error"; message: string };

export default function AuthGate({
  callbackUrl,
  callbackError,
  children,
}: {
  callbackUrl: string | null;
  callbackError: string | null;
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialLocation = useRef({
    pathname: location.pathname,
    search: location.search,
  });
  const callbackAttempt = useRef<Promise<string> | null>(null);
  const [view, setView] = useState<AuthView>({ kind: "checking" });

  const publishState = useCallback(() => {
    const next = authState();
    setView(
      next.authenticated
        ? { kind: "authed", auth: next }
        : { kind: "signed-out" },
    );
  }, []);

  useEffect(() => subscribeAuth(publishState), [publishState]);

  useEffect(() => {
    let cancelled = false;
    async function checkAuth() {
      try {
        await initializeAuth();
        if (cancelled) return;
        if (
          initialLocation.current.pathname === "/auth/callback" &&
          isAuthEnabled()
        ) {
          if (callbackError) throw new Error(callbackError);
          if (!callbackUrl) throw new Error("OAuth response is missing");
          setView({ kind: "callback" });
          const returnTo = await completeCallbackOnce(callbackAttempt, () =>
            completeSignIn(callbackUrl),
          );
          if (!cancelled) {
            publishState();
            navigate(returnTo, { replace: true });
          }
          return;
        }
        if (!cancelled) {
          publishState();
        }
      } catch (error) {
        if (!cancelled) {
          if (initialLocation.current.pathname === "/auth/callback") {
            navigate("/login", { replace: true });
          }
          setView({
            kind: "error",
            message: readableAuthError(error),
          });
        }
      }
    }
    void checkAuth();
    return () => {
      cancelled = true;
    };
  }, [callbackError, callbackUrl, navigate, publishState]);

  const returnTo = useMemo(
    () => safeSignInReturnTo(location.pathname, location.search),
    [location.pathname, location.search],
  );

  if (view.kind === "authed") {
    const principalKey = securityPrincipalKey(
      view.auth.identity,
      view.auth.roles,
    );
    return (
      <PrincipalQueryBoundary key={principalKey}>
        <AuthStateProvider value={view.auth}>{children}</AuthStateProvider>
      </PrincipalQueryBoundary>
    );
  }
  if (view.kind === "signed-out") return <LoginScreen returnTo={returnTo} />;
  if (view.kind === "error")
    return <LoginScreen returnTo={returnTo} error={view.message} />;
  return (
    <div className="login-screen">
      <div className="login-card" role="status" aria-live="polite">
        <h1>
          Isara Modeling <small>sign in</small>
        </h1>
        <p className="login-sub">
          {view.kind === "callback"
            ? "Completing sign in..."
            : "Loading sign in..."}
        </p>
      </div>
    </div>
  );
}

export function safeSignInReturnTo(pathname: string, search: string): string {
  if (pathname === "/auth/callback") return "/";
  if (pathname === "/login") return "/";
  return `${pathname}${search}`;
}

function readableAuthError(error: unknown): string {
  if (error instanceof ErrorResponse && error.error === "access_denied") {
    return (
      "This account is not invited to Isara Modeling yet. " +
      "Ask your Isara engagement team to update your access."
    );
  }
  return error instanceof Error ? error.message : "Sign-in failed";
}

/**
 * Keep every effective principal in a physically separate QueryClient. Merely
 * clearing a shared cache is insufficient: an old mutation can settle after
 * logout and run its onSuccess callback. Its captured client is harmless once
 * this keyed boundary has detached it from the next session.
 */
export function PrincipalQueryBoundary({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createAppQueryClient);

  useEffect(
    () => () => {
      void queryClient.cancelQueries();
      queryClient.clear();
    },
    [queryClient],
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
function LoginScreen({
  returnTo,
  error,
}: {
  returnTo: string;
  error?: string;
}) {
  const login = useLogin();
  const submit = () => login.mutate(returnTo);
  const mutationError =
    login.error instanceof Error ? login.error.message : null;

  return (
    <div className="login-screen">
      <div className="login-card">
        <h1>
          Isara Modeling <small>sign in</small>
        </h1>
        <p className="login-sub">
          Use the account your Isara engagement team invited.
        </p>
        {(error || mutationError) && (
          <div className="banner err" role="alert">
            {error || mutationError}
          </div>
        )}
        <button
          type="button"
          className="login-submit"
          disabled={login.isPending}
          onClick={submit}
        >
          {login.isPending ? "Redirecting…" : "Sign in"}
        </button>
      </div>
    </div>
  );
}
